import React, { useEffect, useState, useMemo, useCallback } from "react";
import { MathJax, MathJaxContext } from "better-react-mathjax";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import API_BASE_URL from "./config"; // Assuming this file exists and provides your API base URL
import { v4 as uuidv4 } from "uuid"; // Import uuid for generating unique IDs
import {
  fetchClassesThunk,
  fetchSubjectsByClassThunk,
  fetchSectionsBySubjectThunk,
  fetchChaptersThunk,
} from "../components/chapter/redux/chapterThunk";
import { fetchCategoriesThunk } from "./categories/redux/categoryThunk";
import toast, { Toaster } from "react-hot-toast";

import {
  setSelectedClassId,
  setSelectedSubjectId,
  setSelectedChapterId,
  setSubjects,
  setSelectedSectionId,
  setSectoins,
  setChapters,
} from "../components/chapter/redux/chapterSlice";

import {
  addQuestionThunk,
  editQuestionThunk,
  fetchQuestionsThunk,
} from "../components/questionBank/redux/questionBankThunk";

// Map difficulty names to IDs (assuming these IDs exist in your difficulty_master table)
const difficultyMapping = {
  easy: 1,
  medium: 2,
  hard: 3,
  // Add more if you have them
};

// const typeMap = {
//   1: "MCQ",
//   3: "True/False",
//   4: "Short Answer",
//   5: "Long Answer",
//   8: "One Word Answer",
//   9: "Definition of Terms",
//   10: "Answer in Brief",
//   11: "Difference Between ",
//   12: "Draw Diagram ",
//   13: "Label Diagram ",
//   14: "Writing Section",
//   15: "Fill in the blanks",
//   16: "match the following",
//   17: "activity based questions",
//   32: "hots",
//   33: "numericals",
// };

// const questionTypeMapping = {
//   "fill in the blanks": 15,
//   "one word answer": 8,
//   "answer in brief": 10,
//   "definition of terms": 9,
//   "difference between": 11,
//   "multiple choice": 1,
//   "true/false": 3,
//   "short answer": 4,
//   "long answer": 5,
//   "draw diagram": 12,
//   "label diagram": 13,
//   "writing section": 14,
//   "match the following": 16,
//   "activity based questions": 17,
//   "map questions": 31,
//   "hots": 32,
//    "higher order thinking skills": 32,
//     "numericals": 33,
//      "Name the Following" : 35
// };

const cellStyle = {
  border: "1px solid #ccc",
  padding: "10px",
  verticalAlign: "top",
  textAlign: "left",
};

const QuestionUpload = ({ onUploadSuccess }) => {
  const [showModal, setShowModal] = useState(false);
  const [loadingUpload, setloadingUpload] = useState(false);
  const [uploadedData, setUploadedData] = useState(null);
  const [editQuestionData, setEditQuestionData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  const dispatch = useDispatch();
  const {
    classes,
    subjects,
    sections,
    chapters,
    selectedClassId,
    selectedSubjectId,
    selectedSectionId,
    selectedChapterId,
    loadingClasses,
    loadingSubjects,
    loadingSections,
    loadingChapters,
  } = useSelector((state) => state.chapter);

  const [error, setError] = useState(null);

  const { categories } = useSelector((state) => state.category);

  // Fetch categories on mount
  useEffect(() => {
    dispatch(fetchCategoriesThunk());
  }, [dispatch]);

  // ✅ Build dynamic type maps after categories are fetched
  const typeMap = useMemo(() => {
    const map = {};
    categories.forEach((item) => {
      map[item.type_id] = item.type_name;
    });
    return map;
  }, [categories]);

  const questionTypeMapping = useMemo(() => {
    const map = {};
    categories.forEach((item) => {
      const name = item.type_name.toLowerCase().trim();
      map[name] = item.type_id;
    });

    return map;
  }, [categories]);

  // // ✅ You can debug it once
  // useEffect(() => {
  //   console.log("✅ Dynamic typeMap:", typeMap);
  //   console.log("✅ Dynamic questionTypeMapping:", questionTypeMapping);
  // }, [typeMap, questionTypeMapping]);

  // --- API Fetch Utility Function ---
  const fetchData = useCallback(
    async (endpoint, setter, loadingSetter, params = "") => {
      loadingSetter(true);
      setError(null); // Clear error before new fetch
      try {
        const url = `${API_BASE_URL}/${endpoint}${params ? `?${params}` : ""}`;
        const response = await fetch(url);
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.message || `HTTP error! Status: ${response.status}`
          );
        }
        const data = await response.json();
        setter(data);
        return data;
      } catch (err) {
        console.error(`Error fetching ${endpoint} in QuestionUpload:`, err);
        setError(`Failed to load ${endpoint}: ${err.message}`);
        setter([]);
        return [];
      } finally {
        loadingSetter(false);
      }
    },
    [API_BASE_URL]
  );

  // Fetch Classes on modal open
  useEffect(() => {
    dispatch(fetchClassesThunk()).then((action) => {
      if (
        fetchClassesThunk.fulfilled.match(action) &&
        action.payload.length > 0
      ) {
        // Preselect first class if none selected
        if (!selectedClassId) {
          dispatch(setSelectedClassId(action.payload[0].class_id.toString()));
        }
      }
    });
  }, [dispatch]);

  // Fetch Subjects when class is selected
  useEffect(() => {
    dispatch(setSubjects([]));
    dispatch(setSelectedSubjectId(""));
    dispatch(setChapters([]));
    dispatch(setSelectedChapterId(""));

    if (selectedClassId) {
      dispatch(fetchSubjectsByClassThunk(selectedClassId)).then((action) => {
        if (
          fetchSubjectsByClassThunk.fulfilled.match(action) &&
          action.payload.length > 0
        ) {
          dispatch(
            setSelectedSubjectId(action.payload[0].subject_id.toString())
          );
          dispatch(
            fetchSectionsBySubjectThunk({ subjectId: selectedSubjectId })
          );
        }
      });
    }
  }, [dispatch, selectedClassId]);

  // Fetch Chapters when subject is selected
  useEffect(() => {
    dispatch(setChapters([]));
    dispatch(setSelectedChapterId(""));

    if (selectedClassId && selectedSubjectId) {
      dispatch(
        fetchChaptersThunk({
          classId: selectedClassId,
          subjectId: selectedSubjectId,
        })
      ).then((action) => {
        if (
          fetchChaptersThunk.fulfilled.match(action) &&
          action.payload.length > 0
        ) {
          dispatch(
            setSelectedChapterId(action.payload[0].chapter_id.toString())
          );
        }
      });
    }
  }, [dispatch, selectedClassId, selectedSubjectId]);

  useEffect(() => {
    if (showModal) {
      const savedData = localStorage.getItem("uploadedQuestions");
      if (savedData) {
        try {
          setUploadedData(JSON.parse(savedData));
        } catch (error) {
          console.error("Error parsing saved data from local storage:", error);
          localStorage.removeItem("uploadedQuestions"); // Clear corrupted data
        }
      }
    }
  }, [showModal]);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${API_BASE_URL}/upload`, formData);
      let data = response.data;

      // Assign unique client-side IDs if not already present
      if (data && Array.isArray(data.questions)) {
        data.questions = data.questions.map((q) => ({
          ...q,
          id: q.id || uuidv4(), // Use existing ID or generate a new one
        }));
      }

      setUploadedData(data);
      localStorage.setItem("uploadedQuestions", JSON.stringify(data));
      setIsEditing(false); // Ensure edit mode is off after new upload
      setEditQuestionData(null); // Clear edit data
      setError(null); // Clear any previous errors
    } catch (error) {
      console.error("File upload failed:", error);
      setError(`File upload failed: ${error.message || "Unknown error"}`);
    }
  };

  const handleUpload = async () => {
    const userStr = localStorage.getItem("user");
    const userId = userStr ? JSON.parse(userStr).id : null;

    if (!userId || !uploadedData || !uploadedData.questions?.length) {
      setError("Missing user ID or uploaded questions.");
      return;
    }

    if (!selectedClassId || !selectedSubjectId || !selectedChapterId) {
      setError("Please select a Class, Subject, and Chapter before uploading.");
      return;
    }
    setloadingUpload(true);
    const toastId = toast.loading("Uploading questions...");
    const questionsToUpload = uploadedData.questions.map((q) => ({
      ...q,
      question_typeid:
        questionTypeMapping[q.question_type?.toLowerCase()?.trim()] ||
        q.question_typeid ||
        null,
      // Assign class_id, subject_id, chapter_id from modal selections
      class_id: parseInt(selectedClassId, 10),
      subject_id: parseInt(selectedSubjectId, 10),
      chapter_id: parseInt(selectedChapterId, 10),
      created_by: userId, // Ensure created_by is on each question for backend logic if needed
    }));
    const payload = {
      created_by: userId,
      class_id: parseInt(selectedClassId, 10),
      subject_id: parseInt(selectedSubjectId, 10),
      section_id: parseInt(selectedSectionId, 10) || null,
      chapter_id: parseInt(selectedChapterId, 10),
      questions: questionsToUpload,
    };

    try {
      const response = await axios.post(
        `${API_BASE_URL}/questions/bulk-upload-v2`,
        payload
      );
      //console.log("Upload success:", response.data);
      toast.success("✅ Questions uploaded successfully!", { id: toastId });
      dispatch(
        fetchQuestionsThunk({
          classId: selectedClassId,
          subjectId: selectedSubjectId,
          chapterId: selectedChapterId,
          sectionId: selectedSectionId ? selectedSectionId : undefined,
        })
      );
      setUploadedData(null);
      closeModal(); // Use closeModal to reset states properly
      setError("Questions uploaded successfully."); // Display success message

      if (onUploadSuccess) {
        onUploadSuccess(); // Callback to tell parent component (QuestionBank) to re-fetch
      }
    } catch (error) {
      console.error("Upload failed:", error);
      setError(
        `Failed to upload questions: ${
          error.message || "An error occurred during upload."
        }`
      );
    } finally {
      setloadingUpload(false);
    }
  };

  const handleEditQuestion = (question) => {
    setEditQuestionData(question); // Set the question data for editing
    setIsEditing(true); // Indicate that we are in edit mode
    // Note: setShowModal(true) is already true if we're calling this from within the modal
    console.log("Editing data:", question);
  };

  const handleUpdateQuestion = (updatedQuestion) => {
    if (!uploadedData) return;

    const updatedQuestions = uploadedData.questions.map((q) =>
      q.id === updatedQuestion.id ? updatedQuestion : q
    );
    const newUploadedData = { ...uploadedData, questions: updatedQuestions };

    setUploadedData(newUploadedData);
    localStorage.setItem("uploadedQuestions", JSON.stringify(newUploadedData));
    setIsEditing(false); // Exit edit mode
    setEditQuestionData(null); // Clear edit data
  };

  const handleDeleteQuestion = (questionId) => {
    if (!uploadedData || !uploadedData.questions) {
      console.warn("No uploaded data or questions to delete from.");
      return;
    }

    // Filter out the question with the matching questionId
    const updatedQuestions = uploadedData.questions.filter(
      (q) => q.id !== questionId
    );
    const updatedData = { ...uploadedData, questions: updatedQuestions };

    setUploadedData(updatedData);
    localStorage.setItem("uploadedQuestions", JSON.stringify(updatedData));
    console.log("Deleted question with ID:", questionId);
  };

  const closeModal = () => {
    setShowModal(false);
    setIsEditing(false); // Reset edit mode on close
    setEditQuestionData(null); // Clear edit data on close
    setUploadedData(null); // Clear all uploaded data
    localStorage.removeItem("uploadedQuestions"); // Remove from local storage
    setError(null); // Clear errors on close
    // Reset category selections
    setSelectedClassId("");
    setSelectedSubjectId("");
    setSelectedChapterId("");
  };

  // Component for editing a single question (can be moved to a separate file)
  const EditQuestionForm = ({
    question,
    onUpdate,
    onCancel,
    classes,
    fetchData,
    loadingClasses,
  }) => {
    const [formData, setFormData] = useState(question);
    // Internal states for dropdowns in edit form, initialized from question data
    const [internalSelectedClassId, setInternalSelectedClassId] = useState(
      question.class_id ? question.class_id.toString() : ""
    );
    const [internalSelectedSubjectId, setInternalSelectedSubjectId] = useState(
      question.subject_id ? question.subject_id.toString() : ""
    );
    const [internalSelectedChapterId, setInternalSelectedChapterId] = useState(
      question.chapter_id ? question.chapter_id.toString() : ""
    );

    const [editFormSubjects, setEditFormSubjects] = useState([]);
    const [editFormChapters, setEditFormChapters] = useState([]);
    const [editFormLoadingSubjects, setEditFormLoadingSubjects] =
      useState(false);
    const [editFormLoadingChapters, setEditFormLoadingChapters] =
      useState(false);

    // Fetch subjects for the pre-selected class when the form loads or class changes
    useEffect(() => {
      if (internalSelectedClassId) {
        fetchData(
          "subjects",
          setEditFormSubjects,
          setEditFormLoadingSubjects,
          `class_id=${internalSelectedClassId}`
        ).then((data) => {
          // If the existing subject is not in the fetched list, reset selection
          if (
            internalSelectedSubjectId &&
            !data.some(
              (s) => s.subject_id.toString() === internalSelectedSubjectId
            )
          ) {
            setInternalSelectedSubjectId("");
          }
        });
      } else {
        setEditFormSubjects([]);
        setInternalSelectedSubjectId("");
      }
    }, [internalSelectedClassId, fetchData, internalSelectedSubjectId]); // Added internalSelectedSubjectId to dependencies

    // Fetch chapters for the pre-selected subject when the form loads or subject changes
    useEffect(() => {
      if (internalSelectedSubjectId && internalSelectedClassId) {
        fetchData(
          "chapters",
          setEditFormChapters,
          setEditFormLoadingChapters,
          `subject_id=${internalSelectedSubjectId}&class_id=${internalSelectedClassId}`
        ).then((data) => {
          // If the existing chapter is not in the fetched list, reset selection
          if (
            internalSelectedChapterId &&
            !data.some(
              (c) => c.chapter_id.toString() === internalSelectedChapterId
            )
          ) {
            setInternalSelectedChapterId("");
          }
        });
      } else {
        setEditFormChapters([]);
        setInternalSelectedChapterId("");
      }
    }, [
      internalSelectedSubjectId,
      internalSelectedClassId,
      fetchData,
      internalSelectedChapterId,
    ]); // Added internalSelectedChapterId to dependencies

    const handleChange = (e) => {
      const { name, value } = e.target;
      setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleOptionChange = (index, e) => {
      const newOptions = [...formData.options];
      newOptions[index].text = e.target.value;
      setFormData((prev) => ({ ...prev, options: newOptions }));
    };

    const handleClassChange = (e) => {
      const classId = e.target.value;
      setInternalSelectedClassId(classId);
      setInternalSelectedSubjectId(""); // Reset subject when class changes
      setInternalSelectedChapterId(""); // Reset chapter when class changes
      setFormData((prev) => ({ ...prev, class_id: parseInt(classId, 10) }));
    };

    const handleSubjectChange = (e) => {
      const subjectId = e.target.value;
      setInternalSelectedSubjectId(subjectId);
      setInternalSelectedChapterId(""); // Reset chapter when subject changes
      setFormData((prev) => ({ ...prev, subject_id: parseInt(subjectId, 10) }));
    };

    const handleChapterChange = (e) => {
      const chapterId = e.target.value;
      setInternalSelectedChapterId(chapterId);
      setFormData((prev) => ({ ...prev, chapter_id: parseInt(chapterId, 10) }));
    };

    const handleSubmit = (e) => {
      e.preventDefault();
      // Combine formData with updated class, subject, chapter IDs
      const finalUpdatedQuestion = {
        ...formData,
        class_id: internalSelectedClassId
          ? parseInt(internalSelectedClassId, 10)
          : null,
        subject_id: internalSelectedSubjectId
          ? parseInt(internalSelectedSubjectId, 10)
          : null,
        chapter_id: internalSelectedChapterId
          ? parseInt(internalSelectedChapterId, 10)
          : null,
      };
      onUpdate(finalUpdatedQuestion); // Pass the updated question back to the parent
      onCancel(); // Close the form
    };

    return (
      <form
        onSubmit={handleSubmit}
        style={{
          marginTop: "20px",
          borderTop: "1px solid #eee",
          paddingTop: "20px",
        }}
      >
        <h3>Edit Question (ID: {formData.id})</h3>
        <div style={{ marginBottom: "10px" }}>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Question Text:
          </label>
          <textarea
            name="question_text"
            value={formData.question_text || ""}
            onChange={handleChange}
            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            style={{ minHeight: "80px" }}
          />
        </div>
        {Array.isArray(formData.options) && formData.options.length > 0 && (
          <div style={{ marginBottom: "10px" }}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Options:
            </label>
            {formData.options.map((opt, index) => (
              <input
                key={index}
                type="text"
                value={opt.text || ""}
                onChange={(e) => handleOptionChange(index, e)}
                className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-2"
              />
            ))}
          </div>
        )}
        <div style={{ marginBottom: "10px" }}>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Question Type:
          </label>
          <select
            name="question_typeid"
            value={formData.question_typeid || ""}
            onChange={handleChange}
            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {Object.entries(typeMap).map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </select>
        </div>
        <div style={{ marginBottom: "10px" }}>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Difficulty:
          </label>
          <select
            name="difficulty_id"
            value={formData.difficulty_id || ""}
            onChange={handleChange}
            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {/* Use difficultyMapping for options */}
            {Object.entries(difficultyMapping).map(([name, id]) => (
              <option key={id} value={id}>
                {name.charAt(0).toUpperCase() + name.slice(1)}
              </option>
            ))}
          </select>
        </div>

        {/* Class, Subject, Chapter Selects */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <div>
            <label
              htmlFor="edit-class-select"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Class:
            </label>
            <select
              id="edit-class-select"
              value={internalSelectedClassId}
              onChange={handleClassChange}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={loadingClasses}
            >
              <option value="">-- Select Class --</option>
              {loadingClasses ? (
                <option disabled>Loading classes...</option>
              ) : classes.length === 0 ? (
                <option disabled>No classes available</option>
              ) : (
                classes.map((cls) => (
                  <option key={cls.class_id} value={cls.class_id.toString()}>
                    {cls.class_name}
                  </option>
                ))
              )}
            </select>
          </div>
          <div>
            <label
              htmlFor="edit-subject-select"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Subject:
            </label>
            <select
              id="edit-subject-select"
              value={internalSelectedSubjectId}
              onChange={handleSubjectChange}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={
                !internalSelectedClassId ||
                editFormLoadingSubjects ||
                editFormSubjects.length === 0
              }
            >
              <option value="">-- Select Subject --</option>
              {editFormLoadingSubjects ? (
                <option disabled>Loading subjects...</option>
              ) : editFormSubjects.length === 0 && internalSelectedClassId ? (
                <option disabled>No subjects for this class</option>
              ) : (
                editFormSubjects.map((sub) => (
                  <option
                    key={sub.subject_id}
                    value={sub.subject_id.toString()}
                  >
                    {sub.subject_name}
                  </option>
                ))
              )}
            </select>
          </div>
          <div>
            <label
              htmlFor="edit-chapter-select"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Chapter:
            </label>
            <select
              id="edit-chapter-select"
              value={internalSelectedChapterId}
              onChange={handleChapterChange}
              className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={
                !internalSelectedSubjectId ||
                !internalSelectedClassId ||
                editFormLoadingChapters ||
                editFormChapters.length === 0
              }
            >
              <option value="">-- Select Chapter --</option>
              {editFormLoadingChapters ? (
                <option disabled>Loading chapters...</option>
              ) : editFormChapters.length === 0 &&
                internalSelectedSubjectId &&
                internalSelectedClassId ? (
                <option disabled>No chapters for this subject</option>
              ) : (
                editFormChapters.map((chap) => (
                  <option
                    key={chap.chapter_id}
                    value={chap.chapter_id.toString()}
                  >
                    {chap.chapter_name}
                  </option>
                ))
              )}
            </select>
          </div>
        </div>

        <div className="flex justify-end mt-4">
          <button
            type="submit"
            className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition duration-300 ease-in-out mr-2"
          >
            Save Changes
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 transition duration-300 ease-in-out"
          >
            Cancel
          </button>
        </div>
      </form>
    );
  };

  return (
    <>
      <Toaster position="top-center" reverseOrder={false} />
      <div className="p-4">
        <button
          onClick={() => setShowModal(true)}
          className="bg-blue-600 text-white px-6 py-3 rounded-md hover:bg-blue-700 transition duration-300 ease-in-out shadow-md"
        >
          Upload Questions
        </button>

        {showModal && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-1000 p-4"
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0,0,0,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            }}
          >
            <div
              className="bg-white p-6 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto relative"
              style={{
                background: "#fff",
                padding: "20px",
                width: "90%",
                maxWidth: "1200px",
                maxHeight: "90vh",
                overflowY: "auto",
                borderRadius: "8px",
                position: "relative",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "1rem", // optional for spacing like mb-4
                }}
                className="flex justify-between items-center mb-4 w-full"
              >
                <h2 className="text-2xl font-bold text-gray-800">
                  Upload Question File
                </h2>
                <button
                  onClick={closeModal}
                  className="text-gray-500 hover:text-gray-700 text-3xl leading-none"
                >
                  &times;
                </button>
              </div>

              {error && (
                <div
                  className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4"
                  role="alert"
                >
                  <span className="block sm:inline"> {error}</span>
                </div>
              )}

              {!isEditing && (
                <>
                  {/* Class, Subject, Chapter Filters */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div>
                      <label
                        htmlFor="class-select-modal"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Select Class:
                      </label>
                      <select
                        id="class-select-modal"
                        value={selectedClassId}
                        onChange={(e) => {
                          dispatch(setSelectedClassId(e.target.value));
                          const value = e.target.value;
                          localStorage.setItem("selectedClassId", value);
                        }}
                        className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={loadingClasses}
                      >
                        <option value="">-- Select Class --</option>
                        {loadingClasses ? (
                          <option disabled>Loading classes...</option>
                        ) : classes.length === 0 ? (
                          <option disabled>No classes available</option>
                        ) : (
                          classes.map((cls) => (
                            <option
                              key={cls.class_id}
                              value={cls.class_id.toString()}
                            >
                              {cls.class_name}
                            </option>
                          ))
                        )}
                      </select>
                    </div>
                    <div>
                      <label
                        htmlFor="subject-select-modal"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Select Subject:
                      </label>
                      <select
                        id="subject-select-modal"
                        value={selectedSubjectId}
                        onChange={(e) => {
                          const value = e.target.value;
                          dispatch(setSelectedSubjectId(e.target.value));
                          localStorage.setItem("selectedSubjectId", value);
                        }}
                        className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={
                          !selectedClassId ||
                          loadingSubjects ||
                          subjects.length === 0
                        }
                      >
                        <option value="">-- Select Subject --</option>
                        {loadingSubjects ? (
                          <option disabled>Loading subjects...</option>
                        ) : subjects.length === 0 && selectedClassId ? (
                          <option disabled>No subjects for this class</option>
                        ) : (
                          subjects.map((sub) => (
                            <option
                              key={sub.subject_id}
                              value={sub.subject_id.toString()}
                            >
                              {sub.subject_name}
                            </option>
                          ))
                        )}
                      </select>
                    </div>
                    {sections.length > 0 && (
                      <div>
                        <label
                          htmlFor="section-select"
                          className="block text-sm font-medium text-gray-700 mb-1"
                        >
                          Select Section:
                        </label>

                        <select
                          value={selectedSectionId}
                          onChange={(e) => {
                            const value = e.target.value;
                            localStorage.setItem("selectedSectionId", value);
                            dispatch(setSelectedSectionId(e.target.value));
                          }}
                          className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          disabled={loadingSections}
                        >
                          <option value="">-- All Section --</option>
                          {sections.map((sec) => (
                            <option key={sec.section_id} value={sec.section_id}>
                              {sec.section_name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    <div>
                      <label
                        htmlFor="chapter-select-modal"
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Select Chapter:
                      </label>
                      <select
                        id="chapter-select-modal"
                        value={selectedChapterId}
                        onChange={(e) => {
                          const value = e.target.value;
                          dispatch(setSelectedChapterId(value));
                          localStorage.setItem("selectedChapterId", value);
                        }}
                        className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        disabled={
                          !selectedSubjectId ||
                          !selectedClassId ||
                          loadingChapters ||
                          chapters.length === 0
                        }
                      >
                        <option value="">-- Select Chapter --</option>
                        {loadingChapters ? (
                          <option disabled>Loading chapters...</option>
                        ) : chapters.length === 0 &&
                          selectedSubjectId &&
                          selectedClassId ? (
                          <option disabled>No chapters for this subject</option>
                        ) : (
                          chapters.map((chap) => (
                            <option
                              key={chap.chapter_id}
                              value={chap.chapter_id.toString()}
                            >
                              {chap.chapter_name}
                            </option>
                          ))
                        )}
                      </select>
                    </div>
                  </div>
                  <input
                    type="file"
                    accept=".docx,.xlsx,.csv,.json"
                    onChange={handleFileUpload}
                    className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  />
                </>
              )}

              {isEditing && editQuestionData ? (
                <EditQuestionForm
                  question={editQuestionData}
                  onUpdate={handleUpdateQuestion}
                  onCancel={() => {
                    setIsEditing(false);
                    setEditQuestionData(null);
                  }}
                  classes={classes}
                  fetchData={fetchData}
                  loadingClasses={loadingClasses}
                />
              ) : (
                uploadedData?.questions?.length > 0 && (
                  <div className="mt-6">
                    <h3 className="text-xl font-semibold mb-3 text-gray-700">
                      Preview Uploaded Questions
                    </h3>
                    <div className="table-responsive rounded-lg border border-gray-200">
                      <MathJaxContext
                        config={{
                          loader: { load: ["input/tex", "output/chtml"] },
                          tex: {
                            inlineMath: [
                              ["\\(", "\\)"],
                              ["$", "$"],
                            ],
                          },
                        }}
                      >
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th
                                className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                style={cellStyle}
                              >
                                #
                              </th>
                              <th
                                className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                style={cellStyle}
                              >
                                Question
                              </th>
                              <th
                                className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                style={cellStyle}
                              >
                                Options
                              </th>
                              <th
                                className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                style={cellStyle}
                              >
                                Type
                              </th>
                              <th
                                className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                style={cellStyle}
                              >
                                Class
                              </th>
                              <th
                                className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                style={cellStyle}
                              >
                                Subject
                              </th>
                              <th
                                className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                style={cellStyle}
                              >
                                Chapter
                              </th>
                              <th
                                className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                style={cellStyle}
                              >
                                Difficulty
                              </th>
                              <th
                                className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                                style={{ minWidth: "110px", ...cellStyle }}
                              >
                                Actionsss
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {uploadedData.questions.map((q, index) => (
                              <tr key={q.id} className="hover:bg-gray-100">
                                <td
                                  className="px-4 py-2 whitespace-nowrap"
                                  style={cellStyle}
                                >
                                  {index + 1}
                                </td>

                                {/* ✅ Question text with MathJax */}
                                <td
                                  className="px-4 py-2 text-wrap"
                                  style={cellStyle}
                                >
                                  <MathJax dynamic>
                                    {q.question_text}{" "}
                                    {/* Just the string with LaTeX */}
                                  </MathJax>
                                </td>

                                <td
                                  className="px-4 py-2 text-wrap max-w-xs min-w-[150px] break-words"
                                  style={cellStyle}
                                >
                                  <ol type="A" className="pl-4 list-decimal">
                                    {Array.isArray(q.options) &&
                                      q.options.map((opt, i) => (
                                        <li key={i}>
                                          <MathJax dynamic>{opt.text}</MathJax>
                                        </li>
                                      ))}
                                  </ol>
                                </td>

                                <td
                                  className="px-4 py-2 whitespace-nowrap"
                                  style={cellStyle}
                                >
                                  {typeMap[q.question_typeid] || "Unknown Type"}
                                </td>

                                <td
                                  className="px-4 py-2 whitespace-nowrap"
                                  style={cellStyle}
                                >
                                  {classes.find(
                                    (cls) => cls.class_id === q.class_id
                                  )?.class_name || "N/A"}
                                </td>

                                <td
                                  className="px-4 py-2 whitespace-nowrap"
                                  style={cellStyle}
                                >
                                  {subjects.find(
                                    (sub) => sub.subject_id === q.subject_id
                                  )?.subject_name || "N/A"}
                                </td>

                                <td
                                  className="px-4 py-2 whitespace-nowrap"
                                  style={cellStyle}
                                >
                                  {chapters.find(
                                    (chap) => chap.chapter_id === q.chapter_id
                                  )?.chapter_name || "N/A"}
                                </td>

                                <td
                                  className="px-4 py-2 whitespace-nowrap"
                                  style={cellStyle}
                                >
                                  {Object.keys(difficultyMapping).find(
                                    (key) =>
                                      difficultyMapping[key] === q.difficulty_id
                                  ) || "N/A"}
                                </td>

                                <td
                                  className="px-4 py-2 whitespace-nowrap"
                                  style={cellStyle}
                                >
                                  <div className="flex justify-center space-x-2">
                                    <button
                                      onClick={() => handleEditQuestion(q)}
                                      className="bg-yellow-500 text-white px-3 py-1 rounded-md text-sm hover:bg-yellow-600 transition duration-200 shadow-sm"
                                    >
                                      <i className="bi bi-pencil-square"></i>{" "}
                                      Edit
                                    </button>
                                    <button
                                      onClick={() => handleDeleteQuestion(q.id)}
                                      className="bg-red-500 text-white px-3 py-1 rounded-md text-sm hover:bg-red-600 transition duration-200 shadow-sm"
                                    >
                                      <i className="bi bi-trash"></i> Delete
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </MathJaxContext>
                    </div>
                    <div className="mt-4 text-right">
                      {loadingUpload ? (
                        <p className="text-green-600 font-bold">Uploading...</p>
                      ) : (
                        <button
                          onClick={handleUpload}
                          className="bg-green-600 text-white px-6 py-3 rounded-md hover:bg-green-700 transition duration-300 ease-in-out shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
                          disabled={
                            !uploadedData?.questions?.length ||
                            !selectedClassId ||
                            !selectedSubjectId ||
                            !selectedChapterId
                          }
                        >
                          Upload All Questions
                        </button>
                      )}
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default QuestionUpload;
